import { memo, useMemo } from "react";
import type { FallbackTier, IncidentFeature, IncidentSeverity } from "@chula/shared";
import { PanelHeader } from "./PanelHeader";
import { PanelState, listFeedState } from "./PanelState";

interface Props {
  events: IncidentFeature[];
  loading?: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
  onFlyTo?: (lng: number, lat: number) => void;
  /** Refetch the feed — shown as Retry when the source is offline. */
  onRetry?: () => void;
}

const MAX_ROWS = 5;

const SEV_COLOR: Record<IncidentSeverity, string> = {
  critical: "var(--bad)",
  high: "var(--warn)",
  medium: "var(--data)",
  low: "var(--text-3)",
};

/** Visible text for the severities that need attention — colour is never the only signal. */
const SEV_TAG: Partial<Record<IncidentSeverity, string>> = {
  critical: "CRIT",
  high: "HIGH",
};

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "—";
  const m = Math.round(ms / 60_000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  return `${Math.round(m / 60)}h`;
}

/**
 * TRAFFIC INCIDENTS — live iTIC accidents, closures, and diversions around
 * campus. Distinct source from citizen reports (official traffic authority
 * feed, not self-reported), so it earns its own panel rather than folding
 * into the citizen-report list.
 *
 * Lives in the MAINT sidebar — the lens's own description already names
 * "traffic events" as a maintenance concern separate from citizen reports,
 * which now live solely in the bottom IssueRoll.
 */
export const TrafficIncidentsPanel = memo(function TrafficIncidentsPanel({
  events,
  loading,
  ageMinutes,
  fallbackTier,
  onFlyTo,
  onRetry,
}: Props) {
  const rows = useMemo(
    () => [...events].sort((a, b) => new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime()).slice(0, MAX_ROWS),
    [events],
  );

  const state = listFeedState({ loading, count: rows.length, tier: fallbackTier, ageMinutes });

  if (state === "loading" || state === "empty" || state === "error") {
    return (
      <div className="left-section traffic-incidents-panel">
        <PanelHeader title="TRAFFIC INCIDENTS" ageMinutes={ageMinutes} fallbackTier={fallbackTier ?? null} source="itic" />
        <PanelState kind={state} what="traffic incidents near campus" source="iTIC" ageMinutes={ageMinutes} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div className="left-section traffic-incidents-panel">
      <PanelHeader
        title="TRAFFIC INCIDENTS"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier ?? null}
        source="itic"
        actions={<span className="caption mono">{events.length}</span>}
      />
      {state === "stale" && <PanelState kind="stale" what="incident list" ageMinutes={ageMinutes} />}
      <ul className="traffic-incident-list">
        {rows.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              className="traffic-incident-btn"
              onClick={() => onFlyTo?.(e.lng, e.lat)}
              title={e.description ?? e.title}
            >
              <span className="traffic-incident-dot" style={{ background: SEV_COLOR[e.severity] ?? "var(--text-3)" }} aria-hidden />
              <span className="sr-only">{e.severity} severity:</span>
              <span className="traffic-incident-title">{e.title}</span>
              {SEV_TAG[e.severity] && <span className="sev-tag mono">{SEV_TAG[e.severity]}</span>}
              <span className="traffic-incident-age mono">{timeAgo(e.reportedAt)}</span>
            </button>
          </li>
        ))}
      </ul>
      <span className="caption mono" style={{ color: "var(--ink-low)" }}>
        iTIC Foundation · official traffic authority feed
      </span>
    </div>
  );
});
